import Modal from "components/atoms/modal";
import { CloseIcon } from "components/atoms/Icon";
import Button from "components/atoms/buttons/Button";
import MementoAndMagnetCard from "components/atoms/cards/user/MagnetAndMementoCard";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import { getUserMementosForMagnet } from "apis/magnets";
import { get } from "utils/request";
import Loading from "components/atoms/Loading";
import { useUserContext } from "components/context/UserContext";
import MementoEmpty from "assets/Logos/MementosEmpty.svg";
import { Link } from "react-router-dom";
import { TOAST_CONFIG } from "../../../constants";

type Props = {
  magnetName: string;
  magnetImage: string;
  brandName: string;
  magnetId: string;
  brandId: string;
  isNotificationModalOpen: boolean;
  handleGoToMagnetPage: () => void;
  onNotificationModalClose: () => void;
};

function UserMagnetMementosModal({
  magnetName,
  magnetImage,
  brandName,
  magnetId,
  brandId,
  isNotificationModalOpen,
  handleGoToMagnetPage,
  onNotificationModalClose,
}: Props) {
  const { setCurrentlyViewingMementoId } = useUserContext();

  const handleGetAllMagnetMenentosError = (errorMessage: string) => {
    toast.error(errorMessage, TOAST_CONFIG);
  };

  const {
    isLoading,
    data: allMagnetMementoData,
    error,
    isFetching,
  } = useQuery({
    queryKey: ["memento", magnetId, handleGetAllMagnetMenentosError],
    queryFn: () =>
      get(getUserMementosForMagnet(magnetId), {
        processError: handleGetAllMagnetMenentosError,
      }),
  });

  if (isLoading && isFetching) return <Loading />;

  if (error) return <p>Something went wrong</p>;

  const handleMementoClick = (inventoryId: string) => {
    const mementoIdFromInventory = allMagnetMementoData.find(
      (memento: Memento) => memento.id === inventoryId,
    )?.object_id;
    if (!mementoIdFromInventory) throw new Error("Memento ID not found in inventory");
    setCurrentlyViewingMementoId(mementoIdFromInventory);
  };

  return (
    <Modal open={isNotificationModalOpen} onClose={onNotificationModalClose} closeOnOutsideClick={false}>
      <div className="w-[calc(100vw-30px)] max-w-[480px] rounded-2xl bg-primary p-3 md:p-5 2xl:p-7">
        <div className="mb-4 flex items-start 2xl:mb-7">
          <img
            className="h-12 w-12 rounded-ll border border-white object-cover 2xl:h-14 2xl:w-14"
            src={magnetImage}
            alt={magnetName}
          />
          <div className="mx-1 grow space-y-0 p-1 2xl:mx-3 2xl:space-y-1">
            <Link
              to={`brand/${brandId}/magnet/${magnetId}`}
              title={magnetName}
              className="line-clamp-1 cursor-pointer overflow-clip  font-sora text-sm font-bold leading-7 text-white 2xl:text-lg"
            >
              {magnetName}
            </Link>
            <Link
              to={`brand-public-profile/${brandId}`}
              className="truncate font-satoshi text-xxs font-semibold leading-4 text-white opacity-50 2xl:text-xs"
            >
              {brandName}
            </Link>
          </div>
          {/* eslint-disable-next-line jsx-a11y/tabindex-no-positive */}
          <button type="button" className="p-1" onClick={onNotificationModalClose} tabIndex={1}>
            <CloseIcon className="h-5 w-5 text-white" />
          </button>
        </div>
        {allMagnetMementoData?.length ? (
          <>
            <p className="mb-2 font-sora text-xxs font-semibold text-white 2xl:mb-5 2xl:text-xs">MEMENTOS</p>
            <div className="secondary-scrollbar mb-4 grid h-[280px] grid-cols-2 flex-col gap-x-6 gap-y-5 overflow-y-scroll  md:grid-cols-3  2xl:mb-7 2xl:h-[330px] 2xl:gap-x-8 2xl:gap-y-8">
              {allMagnetMementoData.map((memento: InventoryItem) => (
                <MementoAndMagnetCard
                  key={`featured${memento.id}_mag`}
                  name={memento.name}
                  isMemento
                  imageUrl={memento.image_url}
                  brandName={memento.brand_name}
                  cardAdditionalClasses="magnet-card-background !p-2"
                  isClickable
                  handleOnClick={() => handleMementoClick(memento.id)}
                />
              ))}
            </div>
          </>
        ) : (
          <div className="mt-20 w-full ">
            <img
              className=" mx-auto my-2 aspect-1 w-2/5 font-satoshi font-450"
              src={MementoEmpty}
              alt="empty_mementos"
            />
            <p className="mx-auto mb-16 text-center text-white  opacity-60">
              You don&apos;t have any mementos for <br /> this Magnet now
            </p>
          </div>
        )}
        <div className="flex justify-center">
          <Button
            text="GO TO MAGNET PAGE"
            onClick={handleGoToMagnetPage}
            additionalClasses="xl:text-xxs px-3 py-1 xl:px-5 xl:py-2 2xl:px-7 2xl:py-3"
            variant="magnetiqSolid"
          />
        </div>
      </div>
    </Modal>
  );
}
export default UserMagnetMementosModal;
